import { useEffect, useRef } from 'react';
import useShortcutStore from '@/store/useShortcutStore';

/**
 * Hook for registering a shortcut context.
 *
 * Automatically registers on mount and unregisters on unmount.
 * The context will receive semantic actions (e.g., 'cancel', 'confirm')
 * dispatched by the shortcut system.
 *
 * @param {string} contextId - Unique identifier (e.g., 'page:dashboard', 'modal:config')
 * @param {Object} handlers - Map of action names to handler functions
 * @param {boolean} enabled - Whether the context is active (default: true)
 *
 * @example
 * // In a modal component:
 * useShortcutContext('modal:humanConfig', {
 *   cancel: () => onClose(),
 *   confirm: () => handleSave(),
 * });
 *
 * @example
 * // In a page component:
 * useShortcutContext('page:dashboard', {
 *   cancel: () => openSettings(),
 * });
 *
 * @example
 * // Conditional context (only active when targeting):
 * useShortcutContext('game:targeting', {
 *   cancel: () => exitTargetingMode(),
 * }, isTargeting);
 */
export default function useShortcutContext(contextId, handlers, enabled = true) {
  const registerContext = useShortcutStore((state) => state.registerContext);
  const unregisterContext = useShortcutStore((state) => state.unregisterContext);

  // Use ref to track if we're currently registered
  const isRegisteredRef = useRef(false);

  // Use ref to store latest handlers (avoids re-registering on every render)
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (enabled) {
      // Register with current handlers
      registerContext(contextId, handlersRef.current);
      isRegisteredRef.current = true;
    } else if (isRegisteredRef.current) {
      // Was registered but now disabled - unregister
      unregisterContext(contextId);
      isRegisteredRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (isRegisteredRef.current) {
        unregisterContext(contextId);
        isRegisteredRef.current = false;
      }
    };
  }, [contextId, enabled, registerContext, unregisterContext]);

  // Update handlers if they change while registered
  useEffect(() => {
    if (enabled && isRegisteredRef.current) {
      registerContext(contextId, handlers);
    }
  }, [contextId, handlers, enabled, registerContext]);
}
